// src/features/scope_module/components/CanvasContainer.js
import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useScope } from '../../../contexts/DemoScopeContext';
import { ZoomIn, ZoomOut, Maximize2, Move } from 'lucide-react';

const DEBUG = false;
const LOG = (...a) => DEBUG && console.log('[Canvas]', ...a);

const MIN_SCALE = 0.1;  // Allow zooming out to see full image
const MAX_SCALE = 6;

const CanvasContainer = () => {
  const {
    // images & layers
    loadedImages,
    layersConfig,
    features,
    shouldDrawMinutiaeFeatures,

    // drawing helpers
    drawTrackingPath,
    drawPlaybackPath,

    // playback state
    playbackState,
    analysisEnabled,

    // tool interactions (image-space coords)
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerLeave,

    getActiveDrag,

    // tracking state for showing/hiding canvas
    isTracking,
    hasTrackingCompleted,
  } = useScope();

  // Show canvas when tracking has started or completed
  const showCanvas = isTracking || hasTrackingCompleted || analysisEnabled;

  const wrapRef   = useRef(null);
  const canvasRef = useRef(null);

  // View state - scale is now relative to fit-to-container
  const [view, setView] = useState({
    scale: 1,     // 1 = fit to container
    offsetX: 0,   // visual pan in screen px
    offsetY: 0,
  });
  const viewRef = useRef(view);
  useEffect(() => { viewRef.current = view; }, [view]);

  // Pan mode toggle - when enabled, mouse drag pans instead of drawing
  const [panModeEnabled, setPanModeEnabled] = useState(false);

  // Track the base scale that fits the image to container
  const fitScaleRef = useRef(1);

  // ---------- Calculate fit scale ----------
  const calculateFitScale = useCallback(() => {
    const base = loadedImages?.enhanced;
    const wrap = wrapRef.current;
    if (!base || !wrap) return 1;

    const containerWidth = wrap.clientWidth;
    const containerHeight = wrap.clientHeight;
    const imageWidth = base.width;
    const imageHeight = base.height;

    // Calculate scale to fit image within container with some padding
    const padding = 40; // pixels of padding around the image
    const scaleX = (containerWidth - padding) / imageWidth;
    const scaleY = (containerHeight - padding) / imageHeight;

    return Math.min(scaleX, scaleY, 1); // Don't scale up beyond 1:1
  }, [loadedImages]);

  // ---------- Draw (DPR only). Zoom/pan handled via CSS transform ----------
  const drawSceneRef = useRef(() => {});
  const scheduleDraw = useCallback(() => requestAnimationFrame(() => drawSceneRef.current()), []);

  const ensureCanvasBaseStyle = useCallback(() => {
    const base = loadedImages?.enhanced;
    const canvas = canvasRef.current;
    if (!base || !canvas) return;

    // 1:1 CSS size & center anchor
    canvas.style.position = 'absolute';
    canvas.style.top = '50%';
    canvas.style.left = '50%';
    canvas.style.width  = `${base.width}px`;
    canvas.style.height = `${base.height}px`;
    canvas.style.transformOrigin = 'center center';

    // DPR backing store
    const dpr = window.devicePixelRatio || 1;
    canvas.width  = Math.round(base.width  * dpr);
    canvas.height = Math.round(base.height * dpr);

    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = false;
  }, [loadedImages]);

  const updateCanvasTransform = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { scale, offsetX, offsetY } = viewRef.current;
    const fitScale = fitScaleRef.current;

    // Apply both fit scale and user zoom
    const totalScale = fitScale * scale;

    // Transform: center, then apply combined scale, then apply pan
    canvas.style.transform =
      `translate(-50%, -50%) ` +
      `scale(${totalScale}) ` +
      `translate(${offsetX / totalScale}px, ${offsetY / totalScale}px)`;
  }, []);

  useEffect(() => { updateCanvasTransform(); }, [view, updateCanvasTransform]);

  // client (CSS px) -> image pixels (inverse of CSS transform)
  const clientToImage = useCallback((clientX, clientY) => {
    const canvas = canvasRef.current;
    const base = loadedImages?.enhanced;
    if (!canvas || !base) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const localX = clientX - rect.left;
    const localY = clientY - rect.top;

    const { scale } = viewRef.current;
    const fitScale = fitScaleRef.current;
    const totalScale = fitScale * scale;

    const ix = localX / totalScale;
    const iy = localY / totalScale;

    const x = Math.max(0, Math.min(base.width  - 1, Math.round(ix)));
    const y = Math.max(0, Math.min(base.height - 1, Math.round(iy)));
    return { x, y };
  }, [loadedImages]);

  drawSceneRef.current = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const base = loadedImages?.enhanced;
    if (!canvas || !ctx || !base) return;

    // clear whole backing store
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    LOG('paint', { scale: viewRef.current.scale.toFixed(3), offset: [viewRef.current.offsetX, viewRef.current.offsetY] });

    // Base
    if (layersConfig?.base?.visible && base) {
      ctx.save();
      ctx.globalAlpha = layersConfig?.base?.opacity ?? 1;
      ctx.drawImage(base, 0, 0);
      ctx.restore();
    }

    // Clarity
    if (layersConfig?.clarity?.visible && loadedImages?.clarity) {
      ctx.save();
      ctx.globalAlpha = layersConfig?.clarity?.opacity ?? 0.5;
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(loadedImages.clarity, 0, 0);
      ctx.restore();
    }

    // Heatmaps
    const drawHeat = (img, opacity) => {
      if (!img) return;
      ctx.save();
      ctx.globalAlpha = opacity ?? 1;
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(img, 0, 0);
      ctx.restore();
    };
    if (layersConfig?.heatmap?.visible)       drawHeat(loadedImages?.heatmap,      layersConfig?.heatmap?.opacity);
    if (layersConfig?.heatmapAdd?.visible)    drawHeat(loadedImages?.heatmapAdd,   layersConfig?.heatmapAdd?.opacity);
    if (layersConfig?.heatmapDelete?.visible) drawHeat(loadedImages?.heatmapDelete,layersConfig?.heatmapDelete?.opacity);

    // Quality
    if (layersConfig?.quality?.visible && loadedImages?.quality) {
      ctx.save();
      ctx.globalAlpha = layersConfig?.quality?.opacity ?? 0.9;
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(loadedImages.quality, 0, 0);
      ctx.restore();
    }

    // Minutiae
    if (shouldDrawMinutiaeFeatures && Array.isArray(features) && features.length) {
      ctx.save();
      ctx.globalAlpha = layersConfig?.minutiae?.opacity ?? 1.0;
      ctx.fillStyle = '#00ff00';
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 2;
      for (const f of features) {
        ctx.beginPath();
        ctx.arc(f.x, f.y, 3, 0, Math.PI * 2);
        ctx.fill();
        if (typeof f.angleDeg === 'number') {
          const L = 18;
          const rad = (f.angleDeg * Math.PI) / 180;
          ctx.beginPath();
          ctx.moveTo(f.x, f.y);
          ctx.lineTo(f.x + Math.cos(rad) * L, f.y + Math.sin(rad) * L);
          ctx.stroke();
        }
      }
      ctx.restore();
    }

    // Active drag preview
    const d = getActiveDrag?.();
    if (d?.isDown) {
      ctx.save();
      ctx.globalAlpha = layersConfig?.minutiae?.opacity ?? 1.0;
      ctx.fillStyle = '#00ff00';
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(d.startX, d.startY, 3, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.moveTo(d.startX, d.startY); ctx.lineTo(d.lastX, d.lastY); ctx.stroke();
      ctx.restore();
    }

    // Tracking (live)
    if (layersConfig?.tracking?.visible && typeof drawTrackingPath === 'function') {
      ctx.save();
      drawTrackingPath(ctx);
      ctx.restore();
    }

    // Playback
    if (layersConfig?.playback?.visible && typeof drawPlaybackPath === 'function') {
      ctx.save();
      drawPlaybackPath(ctx);
      ctx.restore();
    }
  };

  // (Re)size / DPR init when base image appears or canvas becomes visible
  useEffect(() => {
    if (!loadedImages?.enhanced || !showCanvas) return;

    // Calculate fit scale
    const newFitScale = calculateFitScale();
    fitScaleRef.current = newFitScale;

    ensureCanvasBaseStyle();
    setView({ scale: 1, offsetX: 0, offsetY: 0 }); // Start at fit-to-container
    scheduleDraw();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedImages?.enhanced, showCanvas]);

  // Recalculate fit scale on container resize
  useEffect(() => {
    const handleResize = () => {
      const newFitScale = calculateFitScale();
      fitScaleRef.current = newFitScale;
      updateCanvasTransform();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateFitScale, updateCanvasTransform]);

  // Redraw on changes
  useEffect(() => { scheduleDraw(); }, [
    scheduleDraw,
    loadedImages,
    layersConfig,
    features,
    shouldDrawMinutiaeFeatures,
    drawTrackingPath,
    drawPlaybackPath,
    playbackState?.isPlaying
  ]);

  // Playback rAF - redraws canvas continuously while playing
  const playRafRef = useRef(0);
  const isPlayingRef = useRef(false);

  // Update isPlayingRef when playback state changes
  useEffect(() => {
    isPlayingRef.current = playbackState?.isPlaying ?? false;
  }, [playbackState?.isPlaying]);

  useEffect(() => {
    const shouldPlay = analysisEnabled && layersConfig?.playback?.visible && playbackState?.isPlaying;

    if (!shouldPlay) {
      if (playRafRef.current) {
        cancelAnimationFrame(playRafRef.current);
        playRafRef.current = 0;
      }
      scheduleDraw(); // Draw final frame
      return;
    }

    // Start the drawing loop
    const loop = () => {
      drawSceneRef.current();
      // Check ref to see if we should continue (more responsive than waiting for effect re-run)
      if (isPlayingRef.current) {
        playRafRef.current = requestAnimationFrame(loop);
      } else {
        playRafRef.current = 0;
      }
    };

    if (!playRafRef.current) {
      playRafRef.current = requestAnimationFrame(loop);
    }

    return () => {
      if (playRafRef.current) {
        cancelAnimationFrame(playRafRef.current);
        playRafRef.current = 0;
      }
    };
  }, [analysisEnabled, layersConfig?.playback?.visible, playbackState?.isPlaying, scheduleDraw]);

  // ----------------- Zoom & Pan (CSS transform) -----------------
  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const onWheel = (e) => {
      if (!e.altKey) return;
      const base = loadedImages?.enhanced;
      if (!base) return;

      const path = e.composedPath?.() || [];
      if (!path.includes(canvas) && !path.includes(wrap)) return;
      e.preventDefault();

      const { scale, offsetX, offsetY } = viewRef.current;
      const step = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale * step));

      setView({ scale: newScale, offsetX, offsetY });
      scheduleDraw();
    };

    window.addEventListener('wheel', onWheel, { passive: false, capture: true });
    return () => window.removeEventListener('wheel', onWheel, { passive: false, capture: true });
  }, [loadedImages, scheduleDraw]);

  // Pan with Alt+Drag or Middle mouse
  const panRef = useRef({ active: false, lastX: 0, lastY: 0 });
  const beginPan = (clientX, clientY) => { panRef.current = { active: true, lastX: clientX, lastY: clientY }; };
  const updatePan = (clientX, clientY) => {
    const dx = clientX - panRef.current.lastX;
    const dy = clientY - panRef.current.lastY;
    panRef.current.lastX = clientX; panRef.current.lastY = clientY;
    setView(v => ({ ...v, offsetX: v.offsetX + dx, offsetY: v.offsetY + dy }));
    scheduleDraw();
  };
  const endPan = () => { panRef.current.active = false; };

  // Pointer wiring
  const handleMouseDown = (e) => {
    // Pan if: Alt key, middle mouse, or pan mode enabled
    if (e.altKey || e.button === 1 || panModeEnabled) {
      beginPan(e.clientX, e.clientY);
      return;
    }
    const p = clientToImage(e.clientX, e.clientY);
    onPointerDown?.(p.x, p.y);
    scheduleDraw();
  };
  const handleMouseMove = (e) => {
    if (panRef.current.active) { updatePan(e.clientX, e.clientY); return; }
    const p = clientToImage(e.clientX, e.clientY);
    onPointerMove?.(p.x, p.y);
    scheduleDraw();
  };
  const handleMouseUp = (e) => {
    if (panRef.current.active) { endPan(); return; }
    const p = clientToImage(e.clientX, e.clientY);
    onPointerUp?.(p.x, p.y);
    scheduleDraw();
  };

  // Keyboard: 'Z' → reset to fit-to-container
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'z' || e.key === 'Z') {
        setView({ scale: 1, offsetX: 0, offsetY: 0 });
        scheduleDraw();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [scheduleDraw]);

  // Zoom control functions
  const handleZoomIn = () => {
    setView(v => ({
      ...v,
      scale: Math.min(MAX_SCALE, v.scale * 1.25)
    }));
    scheduleDraw();
  };

  const handleZoomOut = () => {
    setView(v => ({
      ...v,
      scale: Math.max(MIN_SCALE, v.scale / 1.25)
    }));
    scheduleDraw();
  };

  const handleFitToScreen = () => {
    setView({ scale: 1, offsetX: 0, offsetY: 0 });
    scheduleDraw();
  };

  // Toggle pan mode
  const handleTogglePanMode = () => {
    setPanModeEnabled(prev => !prev);
  };

  // Calculate display zoom percentage
  const displayZoom = Math.round(view.scale * fitScaleRef.current * 100);

  return (
    <div
      ref={wrapRef}
      className="canvas-container"
    >
      {/* Placeholder shown until tracking starts */}
      {!showCanvas && (
        <div className="canvas-placeholder">
          <div className="canvas-placeholder-content">
            <div className="canvas-placeholder-title">
              Click "Start" to begin tracking
            </div>
            <div className="canvas-placeholder-subtitle">
              The fingerprint image will appear once tracking starts
            </div>
          </div>
        </div>
      )}

      {/* Canvas and controls only shown when tracking */}
      {showCanvas && (
        <>
          <canvas
            ref={canvasRef}
            style={{ cursor: panRef.current.active ? 'grabbing' : (panModeEnabled ? 'grab' : 'default') }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={(e) => { if (panRef.current.active) endPan(); onPointerLeave?.(); }}
          />

        </>
      )}

      {/* Zoom controls bar - outside canvas area */}
      {showCanvas && (
        <div className="canvas-controls-bar">
          <button
            className={`canvas-control-btn ${panModeEnabled ? 'active' : ''}`}
            onClick={handleTogglePanMode}
            title={panModeEnabled ? "Pan Mode (ON) - Click to disable" : "Pan Mode (OFF) - Click to drag image"}
          >
            <Move size={14} />
            <span>Pan</span>
          </button>
          <div className="canvas-controls-separator" />
          <button
            className="canvas-control-btn"
            onClick={handleZoomOut}
            title="Zoom Out"
          >
            <ZoomOut size={14} />
          </button>
          <div className="canvas-zoom-display">
            {displayZoom}%
          </div>
          <button
            className="canvas-control-btn"
            onClick={handleZoomIn}
            title="Zoom In"
          >
            <ZoomIn size={14} />
          </button>
          <div className="canvas-controls-separator" />
          <button
            className="canvas-control-btn"
            onClick={handleFitToScreen}
            title="Fit to Screen (Z)"
          >
            <Maximize2 size={14} />
            <span>Fit</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default CanvasContainer;
